import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useGarageStore } from '@/stores/garage.store'
import { usePortalStore } from '@/stores/portal.store'

import { usePriceService } from '@/services/price.service'
import { useUnitOfMeasureService } from '@/services/unit.of.measure.service'

import { fixedDecimal } from '@/lib/utils'
/*
The 'includesVat' field is used to indicate whether the document header works with prices inclusive of VAT or not.
Based on this information, it is decided which fields of `price` and `total` must be kept UNCHANGED after user input.

In the following schemes, the fields that must be kept unchanged are highlighted in BOLD, all other fields are recalculated accordingly.

In the case where the document works with prices without VAT (`includesVat = false`):

  -----------------------------
  | PRICE_NO_VAT | price_vat  |
  -----------------------------
  | TOTAL_NO_VAT | total_vat* |
  -----------------------------

  * in this case, setting 200.00 in total_vat, the system calculates total_no_vat and MUST RE-UPDATE total_vat accordingly to 199.99 (due to rounding).

In the case where the document works with prices with VAT (`includesVat = true`):

  -----------------------------
  | price_no_vat | PRICE_VAT  |
  -----------------------------
  | total_no_vat | TOTAL_VAT* |
  -----------------------------

  * in this case, setting 200.00 in TOTAL_VAT, the system calculates total_no_vat but does not re-update TOTAL_VAT since the user provided the value.

*/

export const useProductService = () => {
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const priceService = usePriceService()
  const garageStore = useGarageStore()
  const portalStore = usePortalStore()
  const { getConversionRatio } = useUnitOfMeasureService()

  const getLaborPrice = (product, currentRowData, priceContext = {}) => {
    if (product.product_type !== registryStore.productTypes.LABOR) return product.price

    const garageReasonRef = currentRowData.garage_reason_ref ?? priceContext.garageReasonRef?.()
    const garageReason = garageStore.garageReasons.find(reason => reason.id === garageReasonRef)
    const interventionTypeRef = currentRowData.intervention_type_ref ?? priceContext.interventionTypeRef?.() ?? garageReason?.intervention_type_ref
    const interventionType = garageStore.interventionTypes.find(type => type.id === interventionTypeRef)

    if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.WARRANTY) {
      return product.warranty_price ?? product.price
    }

    const workType = priceContext.workType?.() ?? currentRowData.work_type ?? garageReason?.work_type
    if (workType === garageStore.workTypesEnum.INTERNAL) {
      return product.internal_price ?? product.price
    }

    return product.price
  }

  const getProductPrice = (product, currentRowData, priceContext = {}) => {
    if (currentRowData.cost_valorization === garageStore.costValorizationsEnum.COST) {
      return product.cost
    }

    return getLaborPrice(product, currentRowData, priceContext)
  }

  const getDiscountFromDiscountPriorityMatrix = (newData, product, customer, is_order = false) => {
    const getDiscountMask = (sd) => {
      let mask = 0

      if (sd.customer_id) mask |= portalStore.discountPropertyEnum.CUSTOMER
      if (sd.is_urgent) mask |= portalStore.discountPropertyEnum.URGENCY
      if (sd.supplier_brand_ref) mask |= portalStore.discountPropertyEnum.BRAND
      if (sd.discount_category_ref) mask |= portalStore.discountPropertyEnum.DISCOUNT_CATEGORY
      if (sd.price_min != null && sd.price_max != null) mask |= portalStore.discountPropertyEnum.MIN_MAX
      if (sd.discount_list_ref) mask |= portalStore.discountPropertyEnum.DISCOUNT_LIST

      return mask
    }

    const matchMask = (sd, newData, product, customer) => {
      if (sd.customer_id && sd.customer_id !== customer?.id) { return false }
      if (sd.discount_category_ref && sd.discount_category_ref !== product.discount_category_ref) { return false }
      if (sd.discount_list_ref && sd.discount_list_ref !== customer?.discount_list_ref) { return false }
      if (sd.supplier_brand_ref && sd.supplier_brand_ref !== product.supplier_brand_ref) { return false }
      if (sd.price_min != null && sd.price_max != null) {
        if (newData.product_price < sd.price_min || newData.product_price > sd.price_max) { return false }
      }
      return true
    }

    const subjectDiscounts = registryStore.subjectDiscounts
                            .filter(sd => sd.type === (is_order ? registryStore.subjectDiscountTypes.BUY : registryStore.subjectDiscountTypes.SELL))
                            .map(sd => ({ ...sd, _mask: getDiscountMask(sd) }))

    if (!subjectDiscounts.length) return

    for (const requiredMask of portalStore.discountPriorityMasks) {
      const match = subjectDiscounts.find(sd => sd._mask === requiredMask && matchMask(sd, newData, product, customer))

      if (match) return match
    }
  }

  const setDiscountValue = (newData, value, currentRowData, customer, skipTotalUpdate = false, is_order = false) => {
    newData.discount_category_ref = value.discount_category_ref
    newData.discount = newData.discount_2 = newData.discount_3 = 0

    const discount = getDiscountFromDiscountPriorityMatrix(newData, value, customer, is_order)

    if (discount) {
      newData.discount = discount?.discount || 0
      newData.discount_2 = discount?.discount_2 || 0
      newData.discount_3 = discount?.discount_3 || 0
    }
    const supplierBrandChargePercentage = registryStore.supplierBrands.find(sb => sb.id === (newData.supplier_brand_ref ?? currentRowData.supplier_brand_ref))?.charge_percentage || 0
    const chargePercentage = (discount?.charge_percentage || 0) + supplierBrandChargePercentage
    setChargePercentageValue(newData, chargePercentage, newData)

    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
  }

  const setCostValorizationValueAsync = async(newData, value, currentRowData, priceContext = {}) => {
    newData.cost_valorization = value
    const costMode = garageStore.costValorizationsEnum.COST
    const priceSourceChanged = currentRowData.cost_valorization !== value && (currentRowData.cost_valorization === costMode || value === costMode)

    if (!currentRowData.product_ref || !priceSourceChanged) {
      updateTotals(newData, currentRowData)
      return
    }

    const currentProduct = await registryStore.fetchProductByIdAsync(currentRowData.product_ref)
    if (currentProduct?.data?.length > 0) {
      const productPrice = getProductPrice(currentProduct.data[0], { ...currentRowData, ...newData }, priceContext)
      setPriceValue(newData, productPrice, currentRowData, true)
    }

    updateTotals(newData, currentRowData)
  }

  const setProductPrice = (newData, value, currentRowData, skipSetPriceValue = false) => {
    const ratio = getConversionRatio(currentRowData.unit_of_measure_ref, newData.unit_of_measure_ref, currentRowData.supplier_brand_ref)
    let newValue = 0

    const baseValue = value ?? currentRowData.product_price

    if (baseValue && ratio) {
      newValue = fixedDecimal(+(baseValue * (1 / ratio)), 6)
    }

    newData.product_price = newValue

    if (newValue && !skipSetPriceValue) {
      setPriceValue(newData, newValue, currentRowData, false, true, false)
    }
  }

  const setPriceValue = (newData, value, currentRowData, skipTotalUpdate = false, skipChargePercentageUpdate = false, skipPriceVatUpdate = false) => {
    newData.price = (!!value && (value >= 0 || currentRowData.document_row_type === webSiteStore.documentRowTypesEnum.EXPENSE)) ? value : 0
    if (!skipPriceVatUpdate) {
      updatePriceVatValue(newData, currentRowData)
    }
    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
    if (!skipChargePercentageUpdate && currentRowData.product_price <= value) {
      if (currentRowData.product_price) {
        const charge = (value / currentRowData.product_price) - 1
        newData.charge_percentage = fixedDecimal(+charge, 6)
      } else {
        newData.charge_percentage = 0
      }
    }
  }

  const setPriceVatValue = (newData, value, currentRowData, skipTotalUpdate = false, skipChargePercentageUpdate = false) => {
    newData.price_vat = (value >= 0 || currentRowData.document_row_type === webSiteStore.documentRowTypesEnum.EXPENSE) ? value : 0
    updatePriceValue(newData, currentRowData)
  }

  const setChargePercentageValue = (newData, value, currentRowData) => {
    newData.charge_percentage = value
    const rawPrice = currentRowData.product_price * (1 + value)
    const newValue = Math.round(rawPrice * 100) / 100
    setPriceValue(newData, newValue, currentRowData, false, true)
  }

  const updatePriceValue = (newData, currentRowData) => {
    const actualData = { ...currentRowData, ...newData }
    const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage
    const newPrice = actualData.price_vat / (1 + (vat_type_percentage || 0))
    setPriceValue(newData, fixedDecimal(+newPrice, 6), currentRowData, true, false, true)
    newData.total_vat = priceService.getTotalNoVatValue({ ...actualData, price: actualData.price_vat })
    newData.total_no_vat = fixedDecimal(+(newData.total_vat / (1 + (vat_type_percentage || 0))))
  }

  const updatePriceVatValue = (newData, currentRowData) => {
    const actualData = { ...currentRowData, ...newData }
    const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage

    newData.price_vat = (actualData.price || 0) * (1 + (vat_type_percentage || 0))
  }

  const setCostValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    newData.cost = value
    updateCostVatValue(newData, currentRowData)
    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
  }

  const updateCostVatValue = (newData, currentRowData) => {
    const actualData = { ...currentRowData, ...newData }
    const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage
    newData.cost_vat = actualData.cost * (1 + (vat_type_percentage || 0))
  }

  const setQuantityValue = (newData, value, currentRowData, skipTotalUpdate = false, skipUpdateTimeQuantities = false) => {
    newData.quantity = value
    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
    if (!skipUpdateTimeQuantities) {
      updateTimeQuantities(newData, currentRowData)
    }
  }

  const setSupersessionQuantityValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    if (value) {
      newData.quantity = currentRowData.quantity * value
    }
    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
  }

  const setVatTypeValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    value = value || currentRowData.vat_type_ref
    newData.vat_type_ref = value
    newData.vat_type_percentage = null
    if (value) {
      newData.vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === value)?.percentage
    }
    updatePriceVatValue(newData, currentRowData)
    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
  }

  const updateTotalNoVatValue = (newData, currentRowData, includesVat = false) => {
    const actualData = { ...currentRowData, ...newData }
    if (includesVat) {
      const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage || 0
      const totalVat = priceService.getTotalVatValueFromPriceVat(actualData)
      newData.total_no_vat = fixedDecimal(+(totalVat / (1 + vat_type_percentage)))
    } else {
      newData.total_no_vat = priceService.getTotalNoVatValue(actualData)
    }
  }

  const updateTotalVatValue = (newData, currentRowData, includesVat = false) => {
    const actualData = { ...currentRowData, ...newData }
    if (includesVat) {
      newData.total_vat = priceService.getTotalVatValueFromPriceVat(actualData)
    } else {
      newData.total_vat = priceService.getTotalVatValue(actualData)
    }
  }

  const updateTotals = (newData, currentRowData) => {
    updateTotalNoVatValue(newData, currentRowData)
    updateTotalVatValue(newData, currentRowData)
  }

  const setTotalNoVatValue = (newData, value, currentRowData) => {
    const actualData = { ...currentRowData, ...newData }
    const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage
    newData.total_no_vat = fixedDecimal(+(value))
    newData.total_vat = fixedDecimal(+(value * (1 + ((vat_type_percentage)))))
    updateTotalDiscountValue(newData, currentRowData)
    const discountFactor = newData.total_discount || 1
    const newPrice = fixedDecimal(+((value / (currentRowData.quantity || 1)) * (1 / discountFactor)), 6)
    setPriceValue(newData, newPrice, currentRowData, true) // skip total update, the total_vat is already set
  }

  const setTotalVatValue = (newData, value, currentRowData, includesVat = true) => {
    const actualData = { ...currentRowData, ...newData }
    const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage
    const totalNoVat = value / (1 + ((vat_type_percentage)))
    newData.total_no_vat = fixedDecimal(+totalNoVat)
    newData.total_vat = fixedDecimal(+(value))
    updateTotalDiscountValue(newData, currentRowData)
    const discountFactor = newData.total_discount || 1
    const newPrice = fixedDecimal(+((includesVat ? totalNoVat : newData.total_no_vat) / (currentRowData.quantity || 1) * (1 / discountFactor)), 6)
    setPriceValue(newData, newPrice, currentRowData, includesVat)
    if (includesVat) newData.price_vat = fixedDecimal(+(value / (currentRowData.quantity || 1) * (1 / discountFactor)), 6)
  }

  const updateTotalDiscountValue = (newData, currentRowData) => {
    const discount1 = newData.discount ?? currentRowData.discount ?? 0
    const discount2 = newData.discount_2 ?? currentRowData.discount_2 ?? 0
    const discount3 = newData.discount_3 ?? currentRowData.discount_3 ?? 0

    const remaining = (1 - discount1) * (1 - discount2) * (1 - discount3)
    const totalDiscount = 1 - remaining

    newData.total_discount = totalDiscount
  }

  const updateEmptyDocumentRow = (newData, value, currentRowData, options) => {
    newData.product_ref = null
    newData.product_text = null
    newData.unit_of_measure_ref = null
    newData.price = 0
    newData.price_vat = 0
    newData.product_price = 0
    newData.product_cost = 0
    newData.quantity = 1
    newData.total_no_vat = 0
    newData.total_vat = 0
    newData.charge_percentage = 0
    newData.discount = 0
    newData.discount_2 = 0
    newData.discount_3 = 0
    newData.total_discount = 0
    newData.urgency_discount = 0
    newData.urgency_discount_2 = 0
    newData.discount_category_ref = null
    newData.vat_type_ref = null
    newData.vat_type_percentage = null
    newData.quantity_minutes = null
    newData.quantity_UL = null
    newData.cost_valorization = garageStore.getDefaultCostValorization()

    if (newData.document_row_type === webSiteStore.documentRowTypesEnum.TEXT || newData.document_row_type === webSiteStore.documentRowTypesEnum.PRODUCT || options?.clearAll) {
      newData.supplier_brand_ref = options?.preserveSupplierBrand ? currentRowData.supplier_brand_ref : null
    }

    if (options.keepDescription && !options.keepDescription.value) {
      newData.description = null
      options.keepDescription.value = true
    }
  }

  const updateTimeQuantities = (newData, currentRowData) => {
    if ((newData.unit_of_measure_ref || currentRowData.unit_of_measure_ref) && (newData.supplier_brand_ref || currentRowData.supplier_brand_ref)) {
      const uom = webSiteStore.unitsOfMeasure.find(uom => uom.id === (newData.unit_of_measure_ref ?? currentRowData.unit_of_measure_ref))
      const supplier_brand = newData.supplier_brand_ref ?? currentRowData.supplier_brand_ref
      const quantity = newData.quantity ?? currentRowData.quantity
      const ul_uom = webSiteStore.unitsOfMeasure.find(uom => uom.code === webSiteStore.unitOfMeasureOfTimeEnum.UL)
      const minutes_uom = webSiteStore.unitsOfMeasure.find(uom => uom.code === webSiteStore.unitOfMeasureOfTimeEnum.MN)
      const hours_uom = webSiteStore.unitsOfMeasure.find(uom => uom.code === webSiteStore.unitOfMeasureOfTimeEnum.HR)
      let ratio = 1
      switch (uom.code) {
        case webSiteStore.unitOfMeasureOfTimeEnum.UL:
          ratio = getConversionRatio(ul_uom.id, minutes_uom.id, supplier_brand)
          newData.quantity_minutes = fixedDecimal(+(quantity * ratio), 6)
          newData.quantity_ul = quantity
          break
        case webSiteStore.unitOfMeasureOfTimeEnum.MN:
          ratio = getConversionRatio(minutes_uom.id, ul_uom.id, supplier_brand)
          newData.quantity_minutes = quantity
          newData.quantity_ul = fixedDecimal(+(quantity * ratio), 6)
          break
        case webSiteStore.unitOfMeasureOfTimeEnum.HR:
          ratio = getConversionRatio(hours_uom.id, ul_uom.id, supplier_brand)
          newData.quantity_minutes = fixedDecimal(+(quantity * 60), 6)
          newData.quantity_ul = fixedDecimal(+(quantity * ratio), 6)
          break

        default:
          newData.quantity_minutes = null
          newData.quantity_ul = null
          break
      }
    }
  }

  const setDocumentRowTypeValue = (newData, value, currentRowData, options) => {
    newData.document_row_type = value
    updateEmptyDocumentRow(newData, value, currentRowData, options)
    if (newData.document_row_type === webSiteStore.documentRowTypesEnum.TEXT) {
      newData.quantity = newData.price = 0
      newData.urgency_type_ref = null
      newData.delivered_quantity = null
      newData.paid = false
      newData.requested_delivery_date = null
      newData.unit_of_measure_ref = null
    }
    if (newData.document_row_type === webSiteStore.documentRowTypesEnum.LABOR && options?.productUnitOfMeasure) {
      newData.unit_of_measure_ref = options.productUnitOfMeasure.value = webSiteStore.getLocalizedUnitsOfMeasure().find(item => item.code === webSiteStore.unitOfMeasureOfTimeEnum.MN)?.id
    } else if (options?.productUnitOfMeasure) {
      newData.unit_of_measure_ref = options.productUnitOfMeasure.value = null
    }
  }

  const setProductValue = (newData, value, currentRowData, skipTotalUpdate = false, skipDiscountUpdate = false, skipVatUpdate = false, options = {}) => {
    if (!value) {
      options = { ...options, clearAll: true }
      updateEmptyDocumentRow(newData, value, currentRowData, options)
      return
    }

    newData.product_ref = value.id
    newData.description = value.name
    newData.product_text = value.product_text
    newData.supplier_brand_ref = registryStore.supplierBrands.find(i => i.id === value.supplier_brand_ref)?.id
    const productPrice = getProductPrice(value, { ...currentRowData, ...newData }, options.priceContext)
    newData.product_price = productPrice
    newData.price = productPrice
    newData.cost = value.cost
    newData.unit_of_measure_ref = value.unit_of_measure_ref
    if (!skipVatUpdate) {
      setVatTypeValue(newData, value.vat_type_ref, currentRowData)
    }
    newData.quantity = currentRowData.quantity || 1
    setSupersessionQuantityValue(newData, value.supersession_quantity, currentRowData, true)

    newData.charge_percentage = 0
    if (!skipDiscountUpdate) {
      setDiscountValue(newData, value, currentRowData, options.customer?.value, true, options.is_order)

      const interventionTypeRef = newData.intervention_type_ref ?? currentRowData.intervention_type_ref
      const interventionType = garageStore.interventionTypes.find(item => item.id === interventionTypeRef)
      if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.WARRANTY) {
        newData.discount = 1
      }

      updateTotalDiscountValue(newData, currentRowData)
    }

    if (!skipTotalUpdate) {
      updateTotals(newData, currentRowData)
    }
  }

  return {
    setChargePercentageValue,
    setCostValorizationValueAsync,
    setCostValue,
    setDocumentRowTypeValue,
    setProductPrice,
    setPriceValue,
    setPriceVatValue,
    setProductValue,
    setQuantityValue,
    setTotalNoVatValue,
    setTotalVatValue,
    setVatTypeValue,
    updateTotalDiscountValue,
    updateTotalNoVatValue,
    updateTotalVatValue,
    updatePriceVatValue,
    updateTimeQuantities,
  }
}
